"use client";

import * as React from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import { cn } from "../cn";
import {
  applyThemePreference,
  readStoredPreference,
  type ThemePreference,
} from "../theme";

const NEXT: Record<ThemePreference, ThemePreference> = {
  dark: "light",
  light: "auto",
  auto: "dark",
};

const ICON: Record<ThemePreference, typeof Moon> = {
  dark: Moon,
  light: Sun,
  auto: Monitor,
};

const LABEL: Record<ThemePreference, string> = {
  dark: "Dark",
  light: "Light",
  auto: "Auto",
};

export interface ThemeToggleProps {
  className?: string;
  /** Called with the new preference after a switch (e.g. to persist to a profile). */
  onChange?: (pref: ThemePreference) => void;
}

/**
 * One-button theme switch that cycles Dark → Light → Auto. Writes the
 * preference (cookie + localStorage) and applies it instantly. Place it in
 * each app's persistent chrome so it's reachable on every page.
 */
export function ThemeToggle({ className, onChange }: ThemeToggleProps) {
  // Start at the SSR default so server + first client render match; sync to the
  // real stored preference after mount (avoids a hydration mismatch).
  const [pref, setPref] = React.useState<ThemePreference>("dark");
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setPref(readStoredPreference());
    setMounted(true);
  }, []);

  const Icon = ICON[pref];
  const next = NEXT[pref];

  function cycle() {
    setPref(next);
    applyThemePreference(next);
    onChange?.(next);
  }

  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={`Theme: ${LABEL[pref]}. Switch to ${LABEL[next]}.`}
      title={mounted ? `Theme: ${LABEL[pref]} (switch to ${LABEL[next]})` : "Theme"}
      className={cn(
        "inline-flex size-8 items-center justify-center rounded-[var(--radius)]",
        "border border-[var(--color-panel-edge)] text-[var(--color-ink-dim)]",
        "transition hover:border-[var(--color-panel-border)] hover:text-[var(--color-ink)]",
        "focus-visible:outline-2 focus-visible:outline-[var(--color-ring)]",
        className,
      )}
    >
      <Icon className="size-4" aria-hidden />
    </button>
  );
}
